<template>
  <div class="payment-adjust-total-history">
    <div class="flex justify-content-end align-items-center gap-2">
      <a-button icon="history" @click="openHistoryModal">Total History</a-button>
      <a-button v-if="!readOnly" type="primary" icon="plus" @click="openAdjustModal">Adjust Payment</a-button>
    </div>

    <a-modal
      title="Adjust Payment"
      :visible="visibleAdjustModal"
      width="420px"
      :destroy-on-close="true"
      @cancel="closeAdjustModal"
    >
      <a-form-model ref="form" :model="form" :rules="rules" autocomplete="off" layout="vertical">
        <a-form-model-item :colon="false" label-align="left" label="Current Total, $">
          <a-input-number
            class="w-full"
            :value="convertedCurrentTotal"
            :precision="2"
            placeholder="-"
            disabled
          />
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="Original Total, $">
          <a-input-number
            class="w-full"
            :value="convertedOriginalTotal"
            :precision="2"
            placeholder="-"
            disabled
          />
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="New Total, $" prop="total">
          <a-input-number v-model="form.total" class="w-full" :precision="2" placeholder="New total" />
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="Comment" prop="comment">
          <a-textarea v-model="form.comment" placeholder="Comment" :auto-size="{ minRows: 3, maxRows: 5 }" />
        </a-form-model-item>
      </a-form-model>

      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="closeAdjustModal">Cancel</a-button>
          <a-button type="primary" :loading="loadingSubmit" @click="submitAdjust">Save Adjustment</a-button>
        </div>
      </template>
    </a-modal>

    <a-modal
      title="Total History"
      :visible="visibleHistoryModal"
      width="820px"
      :destroy-on-close="true"
      :footer="null"
      @cancel="closeHistoryModal"
    >
      <a-table
        class="adjust-total-history-table"
        size="small"
        :columns="columns"
        :data-source="history"
        :pagination="false"
        :loading="loadingHistory"
        :row-key="getRowKey"
        :scroll="{ x: 760 }"
      />
    </a-modal>
  </div>
</template>

<script>
import { Button, FormModel, Input, InputNumber, Modal, notification, Table } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import util from '@/util'

const { mapActions: paymentActions } = createNamespacedHelpers('payment')

const createInitialForm = () => ({
  total: undefined,
  comment: undefined
})

export default {
  components: {
    'a-button': Button,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input-number': InputNumber,
    'a-modal': Modal,
    'a-table': Table,
    'a-textarea': Input.TextArea
  },
  props: {
    paymentId: {
      type: Number,
      default: undefined
    },
    paymentCurrentTotal: {
      type: [Number, String],
      default: undefined
    },
    paymentOriginalTotal: {
      type: [Number, String],
      default: undefined
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      history: [],
      loadingHistory: false,
      loadingSubmit: false,
      visibleAdjustModal: false,
      visibleHistoryModal: false,
      form: createInitialForm(),
      columns: [
        {
          title: 'Changed By',
          key: 'author',
          width: 210,
          customRender: (_, record) => this.formatAuthor(record.author)
        },
        {
          title: 'Date',
          key: 'created_at',
          width: 170,
          customRender: (_, record) => this.formatDate(record.created_at)
        },
        {
          title: 'Reason',
          key: 'comment',
          width: 260,
          customRender: (_, record) => record.comment || '-'
        },
        {
          title: 'Value',
          key: 'new_total',
          width: 120,
          customRender: (_, record) =>
            `${this.formatMoney(record.old_total)}$ -> ${this.formatMoney(record.new_total)}$`
        }
      ],
      rules: {
        total: [
          {
            required: true,
            message: 'New total can not be empty',
            trigger: 'change'
          }
        ],
        comment: [
          {
            required: true,
            message: 'Comment can not be empty',
            trigger: 'change'
          },
          {
            validator: this.validateComment,
            trigger: 'change'
          }
        ]
      }
    }
  },
  watch: {
    paymentId: {
      handler() {
        this.history = []
        this.closeAdjustModal()
        this.closeHistoryModal()
      },
      immediate: true
    }
  },
  computed: {
    convertedCurrentTotal() {
      return this.convertPaymentTotal(this.paymentCurrentTotal)
    },

    convertedOriginalTotal() {
      return this.convertPaymentTotal(this.paymentOriginalTotal)
    }
  },
  methods: {
    ...paymentActions(['getPaymentTotalHistory', 'manualAdjustPaymentTotal']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    getRowKey(record, index) {
      return record.id ?? `${record.created_at}-${index}`
    },

    formatAuthor(author) {
      if (!author) {
        return 'System'
      }

      const name = [author.first_name, author.last_name].filter(Boolean).join(' ')

      return name || author.associate_number || 'System'
    },

    formatDate(date) {
      return util.convertDateTimeToCurrentTimeZoneWithFormat(date, this.$store.state.applicationState.dateTimeFormat) ?? '-'
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      return util.convertDbToMoney(value)
    },

    convertPaymentTotal(value) {
      if (value === null || value === undefined || value === '') {
        return undefined
      }

      return util.convertDbToMoney(value)
    },

    validateComment(rule, value, callback) {
      if (typeof value === 'string' && !value.trim()) {
        callback(new Error('Comment can not be empty'))

        return
      }

      callback()
    },

    openAdjustModal() {
      if (this.readOnly) return
      this.visibleAdjustModal = true
    },

    openHistoryModal() {
      this.visibleHistoryModal = true
      this.fetchHistory()
    },

    closeAdjustModal() {
      this.visibleAdjustModal = false
      this.form = createInitialForm()

      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },

    closeHistoryModal() {
      this.history = []
      this.visibleHistoryModal = false
    },

    async fetchHistory() {
      if (!this.paymentId) {
        return
      }

      this.loadingHistory = true

      try {
        this.history = await this.getPaymentTotalHistory({
          payment_id: this.paymentId
        })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingHistory = false
      }
    },

    async submitAdjust() {
      if (this.readOnly) return

      this.$refs.form.validate(async valid => {
        if (!valid) {
          this.showNotification('warning', 'Validation Error', 'Please fill in all required fields correctly.')

          return
        }

        this.loadingSubmit = true

        try {
          await this.manualAdjustPaymentTotal({
            payment_id: this.paymentId,
            total: Math.round(util.convertMoneyToDb(this.form.total)),
            comment: this.form.comment.trim()
          })

          this.showNotification('success', 'Success', 'Adjusted total has been successfully updated')
          this.closeAdjustModal()
          await this.fetchHistory()
          this.$emit('updated')
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        } finally {
          this.loadingSubmit = false
        }
      })
    }
  }
}
</script>

<style scoped>
.payment-adjust-total-history {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.adjust-total-history-table {
  min-height: 120px;
}

.w-full {
  width: 100%;
}
</style>
